import { useEffect, useRef, useState } from 'react';

interface UseScrollSpyOptions {
  rootMargin?: string;
  threshold?: number;
}

/**
 * Tracks which section is currently in the viewport using IntersectionObserver.
 * Returns the ID of the topmost visible section (in document order).
 */
export function useScrollSpy(sectionIds: string[], options?: UseScrollSpyOptions): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);
  const intersectingRef = useRef<Set<string>>(new Set());
  const idsKey = sectionIds.join(',');

  useEffect(() => {
    if (typeof window === 'undefined' || !sectionIds.length) return;

    const { rootMargin = '-15% 0px -75% 0px', threshold = 0 } = options ?? {};

    const pickActive = () => {
      for (const id of sectionIds) {
        if (intersectingRef.current.has(id)) {
          setActiveId(id);
          return;
        }
      }
      setActiveId(null); // no section in view → reset (e.g. scrolled back to top)
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            intersectingRef.current.add(entry.target.id);
          } else {
            intersectingRef.current.delete(entry.target.id);
          }
        });
        pickActive();
      },
      { rootMargin, threshold },
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
      intersectingRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, options?.rootMargin, options?.threshold]);

  return activeId;
}
